import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Edit, MoreVertical, Target, Trash2, UserPlus } from '../../../bravosIcons';
import type { LibraryWorkoutPlansApi } from '../../hooks/useLibraryWorkoutPlans';
import { isTrainerOwnedLibraryRow } from '../../utils/workoutLibraryRowUtils';

export type WorkoutPlansTableMobileProps = {
  pl: LibraryWorkoutPlansApi;
  profileId: string | undefined;
  selectedWorkoutPlans: Set<string>;
  openWorkoutPlanMenu: string | null;
  setOpenWorkoutPlanMenu: (id: string | null) => void;
  workoutPlanMenuRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  trainerPlanDuplicateInFlight: boolean;
  onSelectAllWorkoutPlans: () => void;
  onSelectWorkoutPlan: (planId: string) => void;
  onEditWorkoutPlan: (planId: string) => void;
  onDuplicateWorkoutPlan: (planId: string) => void;
  onAssignWorkoutPlan: (planId: string) => void;
  onDeleteWorkoutPlan: (planId: string, planName: string) => void;
};

/** Mobile Library — Workout Plans tab table (R8). */
export function WorkoutPlansTableMobile({
  pl,
  profileId,
  selectedWorkoutPlans,
  openWorkoutPlanMenu,
  setOpenWorkoutPlanMenu,
  workoutPlanMenuRefs,
  trainerPlanDuplicateInFlight,
  onSelectAllWorkoutPlans,
  onSelectWorkoutPlan,
  onEditWorkoutPlan,
  onDuplicateWorkoutPlan,
  onAssignWorkoutPlan,
  onDeleteWorkoutPlan,
}: WorkoutPlansTableMobileProps) {
  const navigate = useNavigate();
  const filteredWorkoutPlans = pl.filteredWorkoutPlansByName;

  return (
    <div className="relative">
      <div
        className="overflow-x-auto"
        style={{
          WebkitOverflowScrolling: 'touch',
          width: '100%',
          maxWidth: '100vw',
        }}
      >
        <div style={{ width: 'max-content', minWidth: '100%' }}>
          <table
            className="w-full border-collapse"
            style={{
              minWidth: '900px',
              width: '900px',
            }}
          >
            <thead>
              <tr className="bg-white dark:bg-theme-surface">
                <th className="px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60 w-12">
                  <input
                    type="checkbox"
                    checked={filteredWorkoutPlans.length > 0 && selectedWorkoutPlans.size === filteredWorkoutPlans.length}
                    onChange={onSelectAllWorkoutPlans}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-theme-border dark:border-white/10 text-accent-violet dark:text-accent-lime focus:ring-accent-violet dark:focus:ring-accent-lime"
                  />
                </th>
                <th className="px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60">
                  Name
                </th>
                <th className="px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60">
                  Created
                </th>
                <th className="px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60">
                  Workouts
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-theme-text/70 dark:text-theme-off-white/70 tracking-wider w-12" />
              </tr>
            </thead>
            <tbody className="bg-theme-bg dark:bg-theme-surface divide-y divide-theme-border dark:divide-white/10">
              {filteredWorkoutPlans.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-theme-text dark:text-theme-off-white mb-2">
                      {pl.workoutPlanNameFilter.trim() ? 'No workout plans match your search' : 'No workout plans yet'}
                    </h3>
                    <p className="text-sm text-theme-text/70 dark:text-theme-off-white/70 mb-4">
                      {pl.workoutPlanNameFilter.trim() ? 'Try a different search term.' : 'Get started by creating your first workout plan'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredWorkoutPlans.map((plan) => {
                  const isSelected = selectedWorkoutPlans.has(plan.id);
                  const isMenuOpen = openWorkoutPlanMenu === plan.id;
                  const planIsYou = isTrainerOwnedLibraryRow(plan.createdBy, profileId);
                  return (
                    <tr
                      key={`plan-${plan.id}`}
                      className={`border-b border-theme-border/60 dark:border-white/10 hover:bg-theme-off-white/50 dark:hover:bg-white/5 cursor-pointer ${isSelected ? 'bg-accent-violet/10 dark:bg-accent-lime/10' : ''}`}
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('input[type="checkbox"]') || target.closest('button') || target.closest('.relative')) {
                          return;
                        }
                        navigate(`/trainer-dashboard/workout-plan/${plan.id}`);
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onSelectWorkoutPlan(plan.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-theme-border dark:border-white/10 text-accent-violet dark:text-accent-lime focus:ring-accent-violet dark:focus:ring-accent-lime"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-normal text-slate-600 dark:text-theme-off-white/80">{plan.name}</div>
                        {plan.description && (
                          <div className="text-sm font-normal text-slate-600 dark:text-theme-off-white/80 truncate max-w-xs">{plan.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-slate-600 dark:text-theme-off-white/80">
                        {plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-normal text-slate-600 dark:text-theme-off-white/80">
                        {plan.workoutCount || 0} {plan.workoutCount === 1 ? 'workout' : 'workouts'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                        <div className="relative" ref={(el) => { workoutPlanMenuRefs.current[plan.id] = el; }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenWorkoutPlanMenu(isMenuOpen ? null : plan.id);
                            }}
                            className="p-1 text-theme-text/50 dark:text-theme-off-white/50 hover:text-theme-text dark:hover:text-theme-off-white rounded-full hover:bg-theme-muted dark:hover:bg-white/10"
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>
                          {isMenuOpen && (
                            <>
                              <div
                                className="fixed inset-0 z-[100]"
                                onClick={() => setOpenWorkoutPlanMenu(null)}
                                onTouchStart={(e) => e.stopPropagation()}
                              />
                              <div
                                className="absolute right-0 mt-2 w-48 bg-theme-bg dark:bg-theme-surface rounded-md shadow-lg z-[101] border border-theme-border dark:border-white/10"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditWorkoutPlan(plan.id);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-theme-text dark:text-theme-off-white hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2"
                                >
                                  <Edit className="w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  disabled={trainerPlanDuplicateInFlight}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (trainerPlanDuplicateInFlight) return;
                                    void onDuplicateWorkoutPlan(plan.id);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-theme-text dark:text-theme-off-white hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2"
                                >
                                  <Copy className="w-4 h-4" />
                                  Duplicate
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAssignWorkoutPlan(plan.id);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-theme-text dark:text-theme-off-white hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2"
                                >
                                  <UserPlus className="w-4 h-4" />
                                  Assign to a member
                                </button>
                                {planIsYou && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteWorkoutPlan(plan.id, plan.name);
                                    }}
                                    className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-theme-muted dark:hover:bg-white/10 flex items-center gap-2"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
